"use client"

import { useState, useEffect } from "react"
import { db } from "../firebase"
import { collection, setDoc, doc, getDocs, deleteDoc } from "firebase/firestore"
import {
  FaPlus,
  FaSave,
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaUserTie,
  FaEnvelope,
  FaImage,
  FaLink,
  FaEdit,
  FaTrash,
  FaGripVertical,
  FaEye,
  FaUndo,
} from "react-icons/fa"

const DepartmentUpdate = () => {
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState(null)
  const [facultyData, setFacultyData] = useState([{ name: "", email: "", description: "", imageUrl: "", order: 0 }])
  const [originalData, setOriginalData] = useState([])
  const [editingIndex, setEditingIndex] = useState(null)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [previewMode, setPreviewMode] = useState(false)

  // Load existing faculty data on component mount
  useEffect(() => {
    const loadFacultyData = async () => {
      try {
        setInitialLoading(true)
        const snapshot = await getDocs(collection(db, "faculty"))
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))

        if (data.length > 0) {
          // Sort by order field, fallback to name if order doesn't exist
          const sortedData = data.sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order
            }
            return a.name.localeCompare(b.name)
          })
          setFacultyData(sortedData)
          setOriginalData(JSON.parse(JSON.stringify(sortedData)))
        }
      } catch (error) {
        console.error("Error loading faculty data:", error)
        setSaveStatus("load-error")
        setTimeout(() => setSaveStatus(null), 3000)
      } finally {
        setInitialLoading(false)
      }
    }

    loadFacultyData()
  }, [])

  const handleFacultyChange = (index, field, value) => {
    const updated = [...facultyData]
    updated[index][field] = value
    setFacultyData(updated)
  }

  const handleAddFaculty = () => {
    const newOrder = facultyData.length > 0 ? Math.max(...facultyData.map((f) => f.order || 0)) + 1 : 0
    setFacultyData([...facultyData, { name: "", email: "", description: "", imageUrl: "", order: newOrder }])
    setEditingIndex(facultyData.length)
  }

  const handleRemoveFaculty = (index) => {
    const updated = [...facultyData]
    updated.splice(index, 1)
    // Reorder remaining items
    updated.forEach((faculty, idx) => {
      faculty.order = idx
    })
    setFacultyData(updated)
    if (editingIndex === index) {
      setEditingIndex(null)
    }
  }

  const handleEditFaculty = (index) => {
    setEditingIndex(editingIndex === index ? null : index)
  }

  const handleResetChanges = () => {
    setFacultyData(JSON.parse(JSON.stringify(originalData)))
    setEditingIndex(null)
    setSaveStatus("reset")
    setTimeout(() => setSaveStatus(null), 2000)
  }

  const validateForm = () => {
    for (const faculty of facultyData) {
      if (!faculty.name.trim() || !faculty.email.trim()) {
        setSaveStatus("validation-error")
        setTimeout(() => setSaveStatus(null), 3000)
        return false
      }
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    setSaveStatus(null)

    try {
      const facultyCollectionRef = collection(db, "faculty")

      // Delete all existing documents first
      const existingDocs = await getDocs(facultyCollectionRef)
      for (const docSnapshot of existingDocs.docs) {
        await deleteDoc(doc(facultyCollectionRef, docSnapshot.id))
      }

      // Add updated faculty data with proper ordering
      for (const [index, faculty] of facultyData.entries()) {
        const facultyWithOrder = { ...faculty, order: index }
        const id = faculty.name.replace(/\s+/g, "_").toLowerCase() + "_" + index
        await setDoc(doc(facultyCollectionRef, id), facultyWithOrder)
      }

      setOriginalData(JSON.parse(JSON.stringify(facultyData)))
      setSaveStatus("success")
      setEditingIndex(null)
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (err) {
      console.error("Error saving faculty data:", err)
      setSaveStatus("error")
      setTimeout(() => setSaveStatus(null), 3000)
    } finally {
      setLoading(false)
    }
  }

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = "move"
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === dropIndex) return

    const updated = [...facultyData]
    const draggedItem = updated[draggedIndex]
    updated.splice(draggedIndex, 1)
    updated.splice(dropIndex, 0, draggedItem)

    // Update order values
    updated.forEach((faculty, index) => {
      faculty.order = index
    })

    setFacultyData(updated)
    setDraggedIndex(null)
  }

  const hasChanges = JSON.stringify(facultyData) !== JSON.stringify(originalData)

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-lg text-gray-600 dark:text-gray-400">Loading faculty data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <FaEdit className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-4xl font-bold mb-2">Faculty Management</h1>
                <p className="text-blue-100">Add, edit, and reorder department faculty members</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <FaEye className="w-4 h-4" />
                <span>{previewMode ? "Edit Mode" : "Preview"}</span>
              </button>
              {hasChanges && (
                <button
                  onClick={handleResetChanges}
                  className="bg-red-500/20 hover:bg-red-500/30 backdrop-blur-sm px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                >
                  <FaUndo className="w-4 h-4" />
                  <span>Reset</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {saveStatus && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          {saveStatus === "success" && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center space-x-3">
              <FaCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <p className="text-green-800 dark:text-green-200">Faculty information saved successfully!</p>
            </div>
          )}
          {saveStatus === "error" && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-3">
              <FaExclamationTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <p className="text-red-800 dark:text-red-200">Failed to save faculty information. Please try again.</p>
            </div>
          )}
          {saveStatus === "validation-error" && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-center space-x-3">
              <FaExclamationTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <p className="text-yellow-800 dark:text-yellow-200">
                Please fill in all required fields (Name and Email) for each faculty member.
              </p>
            </div>
          )}
          {saveStatus === "load-error" && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-3">
              <FaExclamationTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <p className="text-red-800 dark:text-red-200">Failed to load existing faculty data.</p>
            </div>
          )}
          {saveStatus === "reset" && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center space-x-3">
              <FaCheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <p className="text-blue-800 dark:text-blue-200">Changes have been reset to original values.</p>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-purple-700"></div>

          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-700 rounded-xl shadow-lg">
                  <FaUserTie className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Faculty Members</h2>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Drag and drop to reorder • Click edit to modify
              </div>
            </div>

            <div className="space-y-6">
              {facultyData.map((faculty, index) => (
                <div
                  key={index}
                  draggable={!previewMode}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`bg-gray-50 dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 transition-all duration-200 ${
                    draggedIndex === index ? "opacity-50 scale-95" : ""
                  } ${editingIndex === index ? "ring-2 ring-blue-500" : ""}`}
                >
                  {/* Faculty Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-4">
                      {!previewMode && (
                        <div className="cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                          <FaGripVertical className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-700 rounded-lg">
                          <FaUserTie className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {faculty.name || `Faculty Member ${index + 1}`}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Position #{index + 1}</p>
                        </div>
                      </div>
                    </div>
                    {!previewMode && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditFaculty(index)}
                          className={`p-2 rounded-lg transition-colors duration-200 ${
                            editingIndex === index
                              ? "bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                              : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-400"
                          }`}
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        {facultyData.length > 1 && (
                          <button
                            onClick={() => handleRemoveFaculty(index)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Faculty Content */}
                  {(editingIndex === index || previewMode) && (
                    <div className="p-6">
                      {editingIndex === index ? (
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Left Column - Form Fields */}
                          <div className="space-y-4">
                            {/* Name Field */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Full Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                className="w-full p-3 rounded-lg bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 text-gray-900 dark:text-white"
                                placeholder="Dr. John Doe"
                                value={faculty.name}
                                onChange={(e) => handleFacultyChange(index, "name", e.target.value)}
                                required
                              />
                            </div>

                            {/* Email Field */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Email Address <span className="text-red-500">*</span>
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                  type="email"
                                  className="w-full pl-10 pr-3 py-3 rounded-lg bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 text-gray-900 dark:text-white"
                                  placeholder="john.doe@university.edu"
                                  value={faculty.email}
                                  onChange={(e) => handleFacultyChange(index, "email", e.target.value)}
                                  required
                                />
                              </div>
                            </div>

                            {/* Description Field */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Description
                              </label>
                              <textarea
                                className="w-full p-3 rounded-lg bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 text-gray-900 dark:text-white"
                                rows="4"
                                placeholder="Professor of Chemical Engineering with expertise in..."
                                value={faculty.description}
                                onChange={(e) => handleFacultyChange(index, "description", e.target.value)}
                              />
                            </div>

                            {/* Image URL Field */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Profile Image URL
                              </label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <FaLink className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                  type="url"
                                  className="w-full pl-10 pr-3 py-3 rounded-lg bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 text-gray-900 dark:text-white"
                                  placeholder="https://example.com/faculty-photo.jpg"
                                  value={faculty.imageUrl}
                                  onChange={(e) => handleFacultyChange(index, "imageUrl", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Right Column - Image Preview */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Profile Photo Preview
                            </label>
                            {faculty.imageUrl ? (
                              <div className="relative">
                                <img
                                  src={faculty.imageUrl || "/placeholder.svg"}
                                  alt="Faculty preview"
                                  className="w-full h-48 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                                  onError={(e) => {
                                    e.target.style.display = "none"
                                    e.target.nextSibling.style.display = "flex"
                                  }}
                                />
                                <div className="w-full h-48 border-2 border-dashed border-red-300 dark:border-red-600 rounded-lg flex items-center justify-center bg-red-50 dark:bg-red-900/20 hidden">
                                  <div className="text-center">
                                    <FaExclamationTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                                    <p className="text-sm text-red-500 dark:text-red-400">Failed to load image</p>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="w-full h-48 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-600">
                                <div className="text-center">
                                  <FaImage className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                                  <p className="text-sm text-gray-500 dark:text-gray-400">No image URL provided</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        // Preview Mode
                        <div className="grid md:grid-cols-3 gap-6">
                          <div className="md:col-span-1">
                            <img
                              src={faculty.imageUrl || "/placeholder.svg"}
                              alt={faculty.name}
                              className="w-full h-48 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.src = "/placeholder.svg"
                              }}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{faculty.name}</h4>
                            <p className="text-blue-600 dark:text-blue-400 mb-4 flex items-center">
                              <FaEnvelope className="w-4 h-4 mr-2" />
                              {faculty.email}
                            </p>
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                              {faculty.description || "No description provided."}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Add Faculty Button */}
              {!previewMode && (
                <button
                  onClick={handleAddFaculty}
                  className="w-full p-6 border-2 border-dashed border-blue-300 dark:border-blue-600 rounded-2xl hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200 flex items-center justify-center space-x-3 text-blue-600 dark:text-blue-400"
                >
                  <FaPlus className="w-6 h-6" />
                  <span className="text-lg font-medium">Add New Faculty Member</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Save Button */}
        {!previewMode && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleSubmit}
              disabled={loading || !hasChanges}
              className="bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 disabled:from-gray-400 disabled:to-gray-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none flex items-center space-x-3"
            >
              {loading ? (
                <>
                  <FaSpinner className="w-6 h-6 animate-spin" />
                  <span>Saving Faculty Data...</span>
                </>
              ) : (
                <>
                  <FaSave className="w-6 h-6" />
                  <span>{hasChanges ? "Save Changes" : "No Changes to Save"}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default DepartmentUpdate
